import clsx from "clsx";
import type { ReactNode } from "react";
import { useRef } from "react";
import { Link } from "wouter";

import { BetSlipDrawer } from "@/betslip/BetSlipDrawer";
import { CashierDialog } from "@/cashier/CashierDialog";
import { ChatDrawer } from "@/chat/ChatDrawer";
import { useRoutePrefetch } from "@/hooks/useRoutePrefetch";
import { AuthDialog } from "@/session/AuthDialog";
import { useSession } from "@/session/SessionProvider";
import { useTheme } from "@/theme/ThemeProvider";

import { HeroSlot } from "./HeroSlot";
import { MobileBottomNav } from "./MobileBottomNav";
import { ProductSidebar } from "./ProductSidebar";
import { useSidebarPreference } from "./useSidebarPreference";
import { V2Header } from "./V2Header";

export function MonkeyTiltV2Shell({ children }: { children: ReactNode }) {
  const { tenantSlug, theme } = useTheme();
  const { loginOpen, dismissLogin } = useSession();
  const prefetchRoute = useRoutePrefetch();
  const desktopToggleRef = useRef<HTMLButtonElement>(null);
  const { collapsed, hasPreference, toggleCollapsed, transitionsEnabled } =
    useSidebarPreference(tenantSlug, theme.preset);

  return (
    <div
      className={clsx(
        "v2-shell",
        collapsed && "is-sidebar-collapsed",
        !transitionsEnabled && "is-sidebar-initializing",
      )}
      data-sidebar-preference={hasPreference ? "stored" : "default"}
    >
      <V2Header />
      <ProductSidebar
        collapsed={collapsed}
        mobileOpen={false}
        onClose={() => undefined}
        onPrefetch={prefetchRoute}
        onToggleCollapsed={toggleCollapsed}
        theme={theme}
        toggleRef={desktopToggleRef}
      />

      <div className="v2-shell__column">
        <main className="v2-shell__main">
          <HeroSlot heroId={theme.heroId} />
          {children}
        </main>
        <footer className="app-footer v2-shell__footer">
          <span>Every race can be recomputed in your browser.</span>
          <Link href="/app/verify">Fairness verifier</Link>
        </footer>
      </div>

      <MobileBottomNav onPrefetch={prefetchRoute} />

      <BetSlipDrawer />
      <CashierDialog />
      <ChatDrawer />
      {loginOpen ? <AuthDialog onClose={dismissLogin} /> : null}
    </div>
  );
}
